'use client';

import dynamic from 'next/dynamic';
import { useEffect } from 'react';

import { Preloader } from '@/components/Preloader/Preloader';
import { uuid } from '@/helpers/common';
import { usePromoRedirect } from '@/hooks';
import useEstonia from '@/hooks/estonia';
import { useNavigation } from '@/hooks/navigation';
import { useServer } from '@/modules/server/context/ServerEnvContext';
import { useTrackingUserParamsHook } from '@/modules/userTrackingParams';
import { useCommonStore } from '@/store';

const MAP_NAME_TO_PROMO_COMPONENT: Record<string, string> = {
  'sun-of-egypt': 'SunOfEgypt',
  'big-bass': 'BigBass',
  'wheel-fortune': 'WheelFortune',
  'dog-treasure': 'DogTreasure',
  'crown-wheel': 'CrownWheel',
  'crown-banner': 'CrownBanner',
  'star-gold-coast': 'StarGoldCoast',
  'wolf-elvis-sun-buffalo': 'WolfElvisSunBuffalo',
  analyzer40: 'April2025',
  crown: 'CrownWheelBlack',
  labsjuicy: 'JuicyMay',
  free40: 'JuicyCarMay2025',
  AVIATORUP: 'AviatorUp',
  'quiz-bonus': 'QuizBonusJune2025',
  'bonus-claim': 'BonusClaim',
  'buffalo-wheel': 'BuffaloWheel',
  lpSlotsCircle: 'SlotsCircle',
  'reg-rewards': 'RegRewards',
  'reg-dragon-rewards': 'RegDragonRewards',
  'crown-reg': 'CrownReg',
  'circle-1': 'Circle',
  'crypto-exclusive': 'CryptoExclusiveMay25',
  cryptowelcome: 'CryptoOfferMay2025',
  lvl1: 'WelcomeApril2025',
  'lvl1-nomobileimage': 'WelcomeApril2025',
  'lvl1-fixedscroll': 'WelcomeApril2025',
  'wolf-wheel': 'WolfWheel',
  // Winter January variations
  askwinter: 'WinterJanuary',
  vmgwinter: 'WinterJanuary',
  lcbwinter: 'WinterJanuary',
  chipywinter: 'WinterJanuary',
  guruwinter: 'WinterJanuary',
  toppwinter: 'WinterJanuary',
  infowinter: 'WinterJanuary',
  helpwinter: 'WinterJanuary',
  wintercode: 'WinterJanuary',
  slotwinter: 'WinterJanuary',
  megawinter: 'WinterJanuary',
  firstwinter: 'WinterJanuary',
  vrswinter: 'WinterJanuary',
  wettwinter: 'WinterJanuary',
  carwinter: 'WinterJanuary',
  valwinter: 'WinterJanuary',
  homdwinter: 'WinterJanuary',
  luckywinter: 'WinterJanuary',
  enterwinter: 'WinterJanuary',
  revwinter: 'WinterJanuary',
  maxwinter: 'WinterJanuary',
  winterlab: 'WinterJanuary',
  shwinter: 'WinterJanuary',
  dreamwinter: 'WinterJanuary',
  newwinter: 'WinterJanuary',
  pushwinter: 'WinterJanuary',
  smartwinter: 'WinterJanuary',
  // Dog Treasure Terms variations
  easywinwett: 'DogTreasureTerms',
  easywinsmart: 'DogTreasureTerms',
  easywincode: 'DogTreasureTerms',
  easywininfo: 'DogTreasureTerms',
  easywindream: 'DogTreasureTerms',
  // Crown March 25 variations
  crownmedia: 'CrownMarch25',
  crownrev: 'CrownMarch25',
  crownlab: 'CrownMarch25',
  crownnew: 'CrownMarch25',
  crownlucky: 'CrownMarch25',
  crownask: 'CrownMarch25',
  crowndream: 'CrownMarch25',
  crownhelp: 'CrownMarch25',
  johnslots: 'CrownMarch25',
  gamblizard: 'CrownMarch25',
  crownmax: 'CrownMarch25',
  crowncode: 'CrownMarch25',
  crownguru: 'CrownMarch25',
  crownsh: 'CrownMarch25',
  crownsmart: 'CrownMarch25',
  crowncar: 'CrownMarch25',
  crownpush: 'CrownMarch25',
  crowntopp: 'CrownMarch25',
  // Banks Benefits May 2025 variations
  power: 'BanksBenefitsMay2025',
  mediapower: 'BanksBenefitsMay2025',
  lcbpower: 'BanksBenefitsMay2025',
  chipypower: 'BanksBenefitsMay2025',
  gurupower: 'BanksBenefitsMay2025',
  gamblpower: 'BanksBenefitsMay2025',
  seopower: 'BanksBenefitsMay2025',
  helppower: 'BanksBenefitsMay2025',
  powercode: 'BanksBenefitsMay2025',
  powerslot: 'BanksBenefitsMay2025',
  'lyzer40-games': 'BanksBenefitsMay2025',
  'powerslot-games': 'BanksBenefitsMay2025',
  'powerslot-winners': 'BanksBenefitsMay2025',
  powerzilla: 'BanksBenefitsMay2025',
  newpower: 'BanksBenefitsMay2025',
  analyzepower: 'BanksBenefitsMay2025',
  'analyzepower-games': 'BanksBenefitsMay2025',
  'analyzepower-winners': 'BanksBenefitsMay2025',
  luckypower: 'BanksBenefitsMay2025',
  askpower: 'BanksBenefitsMay2025',
  'askpower-games': 'BanksBenefitsMay2025',
  'askpower-winners': 'BanksBenefitsMay2025',
  enterpower: 'BanksBenefitsMay2025',
  'enterpower-games': 'BanksBenefitsMay2025',
  'enterpower-winners': 'BanksBenefitsMay2025',
  spicypower: 'BanksBenefitsMay2025',
  maxpower: 'BanksBenefitsMay2025',
  powerlab: 'BanksBenefitsMay2025',
  winpower: 'BanksBenefitsMay2025',
  // Easter April 2025 variations
  spin: 'EasterApril2025',
  spinmedia: 'EasterApril2025',
  spinlcb: 'EasterApril2025',
  spinchipy: 'EasterApril2025',
  spinguru: 'EasterApril2025',
  spintopp: 'EasterApril2025',
  spinhelp: 'EasterApril2025',
  spincode: 'EasterApril2025',
  spinslot: 'EasterApril2025',
  spinmega: 'EasterApril2025',
  spinzilla: 'EasterApril2025',
  spincalendar: 'EasterApril2025',
  spincar: 'EasterApril2025',
  spinhomd: 'EasterApril2025',
  spinlucky: 'EasterApril2025',
  spinask: 'EasterApril2025',
  spinenter: 'EasterApril2025',
  spinrev: 'EasterApril2025',
  spinmax: 'EasterApril2025',
  spinlab: 'EasterApril2025',
  spinsh: 'EasterApril2025',
  spinnew: 'EasterApril2025',
  spinpush: 'EasterApril2025',
  pinsmart: 'EasterApril2025',
  // Circus November variations
  lvlcircus: 'CircusNovember',
  fruitsup: 'CircusNovember',
  // CardsBenefitsJune2025
  grand: 'CardsBenefitsJune2025',
  mediagrand: 'CardsBenefitsJune2025',
  lcbgrand: 'CardsBenefitsJune2025',
  chipygrand: 'CardsBenefitsJune2025',
  gurugrand: 'CardsBenefitsJune2025',
  gamblgrand: 'CardsBenefitsJune2025',
  grandhelp: 'CardsBenefitsJune2025',
  turbolvl: 'CardsBenefitsJune2025',
  grandcode: 'CardsBenefitsJune2025',
  grandslot: 'CardsBenefitsJune2025',
  grandzilla: 'CardsBenefitsJune2025',
  newgrand: 'CardsBenefitsJune2025',
  analyzegrand: 'CardsBenefitsJune2025',
  luckygrand: 'CardsBenefitsJune2025',
  askgrand: 'CardsBenefitsJune2025',
  entergrand: 'CardsBenefitsJune2025',
  spicygrand: 'CardsBenefitsJune2025',
  maxgrand: 'CardsBenefitsJune2025',
  grandlab: 'CardsBenefitsJune2025',
  wingrand: 'CardsBenefitsJune2025',
  'gates-of-olympus': 'GatesOfOlympus',
  spin50: 'Carways2025',
  // Fiesta June 2025 variations
  sun: 'FiestaJune2025',
  sun35fs: 'FiestaJune2025',
  lcbsun: 'FiestaJune2025',
  chipysun: 'FiestaJune2025',
  gurusun: 'FiestaJune2025',
  gamblsun: 'FiestaJune2025',
  helpsun: 'FiestaJune2025',
  suncode: 'FiestaJune2025',
  sunslot: 'FiestaJune2025',
  sunzilla: 'FiestaJune2025',
  newsun: 'FiestaJune2025',
  analyzesun: 'FiestaJune2025',
  luckysun: 'FiestaJune2025',
  asksun: 'FiestaJune2025',
  entersun: 'FiestaJune2025',
  spicysun: 'FiestaJune2025',
  maxsun: 'FiestaJune2025',
  sunlab: 'FiestaJune2025',
  sunwin: 'FiestaJune2025',
  hotsun: 'FiestaJune2025',
  sunny: 'FiestaJune2025',
} as const;

interface IPromoComponentProps {
  name?: string;
}

interface IPromoPageProps {
  params: {
    name: string;
  };
}

export default function PromoPage({ params: { name } }: IPromoPageProps) {
  usePromoRedirect(name);

  useEffect(() => {
    if (!localStorage.getItem('uuid')) {
      localStorage.setItem('uuid', uuid());
    }
    useCommonStore.setState({
      pendingUserPure: false,
      pendingUser: false,
    });
  }, []);

  const { isEstonia } = useEstonia();

  const setCountryCodeData = useCommonStore.use.setCountryCodeData();
  const { countryCode } = useServer();

  useEffect(() => {
    setCountryCodeData(isEstonia);
  }, [setCountryCodeData, countryCode, isEstonia]);

  useTrackingUserParamsHook();

  const { navigate } = useNavigation();

  const componentName = MAP_NAME_TO_PROMO_COMPONENT[name] || null;

  if (componentName) {
    const Component = dynamic<IPromoComponentProps>(
      () =>
        import(`@/components/_promo/pages/${componentName}`).then(
          (mod) => mod[componentName]
        ),
      {
        loading: () => <Preloader />,
      }
    );
    return <Component name={name} />;
  }

  return navigate('/?modal=registration');
}
